import type { BetaFeatureCompany, BetaFeatureListing } from 'types';
import type { AdminBetaFeaturesManager } from '@/state/useAdminBetaFeatures';
import { DataTable, useDataControl } from '@/components/DataTable';
import { ConfirmDialog } from '@/components/Dialogs/ConfirmDialog';
import { cn } from 'helpers/cn';
import { formatDate } from 'helpers/formatters';
import { userFullName } from 'helpers/user-identity';
import { companyReach, useAdminBetaFeatures } from '@/state/useAdminBetaFeatures';
import { ActionButton, EmptyState, MICRO_LABEL_SHAPE, Panel } from '../../Dashboard/ConsolePanel';
import { SectionStatusLine } from '../../Dashboard/Settings/Billing/SectionStatusLine';
import { CompanyPicker } from './CompanyPicker';

/**
 * Super Admin: which companies have which beta feature. One panel per feature in
 * BETA_FEATURES, listing the companies it is switched on for, with an add control (confirmed for
 * an agency) and a confirmed remove. A switch on an agency also reaches the client companies it
 * manages.
 */
export const BetaFeatures = () => {
  const manager = useAdminBetaFeatures();
  const { list } = manager;

  return (
    <div className="flex flex-col gap-[14px]">
      {!list.data ? (
        <Panel title="Beta features">
          <SectionStatusLine
            status={list}
            loadingText="Loading beta features..."
            errorText="Beta features could not be loaded."
          />
        </Panel>
      ) : list.data.length === 0 ? (
        <Panel title="Beta features">
          <EmptyState>No features are in beta right now.</EmptyState>
        </Panel>
      ) : (
        list.data.map((listing) => <FeaturePanel key={listing.feature} listing={listing} manager={manager} />)
      )}

      <ConfirmDialog
        open={!!manager.pendingEnable}
        title={
          manager.pendingEnable
            ? `Switch ${manager.pendingEnable.listing.label} on for ${manager.pendingEnable.company.name}?`
            : ''
        }
        description={manager.pendingEnableConsequence ?? undefined}
        confirmLabel="Switch on"
        onConfirm={manager.confirmEnable}
        onCancel={manager.cancelEnable}
      />

      <ConfirmDialog
        open={!!manager.pendingRemoval}
        title={
          manager.pendingRemoval
            ? `Switch ${manager.pendingRemoval.listing.label} off for ${manager.pendingRemoval.company.name}?`
            : ''
        }
        description={manager.pendingRemovalConsequence ?? undefined}
        confirmLabel="Switch off"
        confirmVariant="destructive"
        onConfirm={manager.confirmRemoval}
        onCancel={manager.cancelRemoval}
      />
    </div>
  );
};

type FeaturePanelProps = { listing: BetaFeatureListing; manager: AdminBetaFeaturesManager };

const FeaturePanel = ({ listing, manager }: FeaturePanelProps) => {
  const dataController = useDataControl<BetaFeatureCompany>(listing.companies);
  const count = listing.companies.length;

  return (
    <Panel
      title={listing.label}
      description={listing.description}
      actions={
        <>
          <span className={cn(MICRO_LABEL_SHAPE, 'rounded-pill border-line text-dim border px-2 py-0.5')}>
            {count} {count === 1 ? 'company' : 'companies'}
          </span>
          <CompanyPicker listing={listing} manager={manager} />
        </>
      }
    >
      {count === 0 ? (
        <EmptyState>No companies have {listing.label} yet.</EmptyState>
      ) : (
        <DataTable<BetaFeatureCompany>
          dataController={dataController}
          uniqueKey="id"
          columns={[
            {
              label: 'Company',
              field: 'name',
              sortKey: 'name',
              mobile: 'title',
              format: ({ item }) => (
                <span className="flex min-w-0 flex-col">
                  <span className="text-foreground truncate font-medium">{item.name}</span>
                  <span className="supporting num">#{item.id}</span>
                </span>
              ),
            },
            { label: 'Reaches', format: ({ item }) => companyReach(item) ?? 'This company only' },
            {
              label: 'Switched on by',
              format: ({ item }) => (item.enabledBy ? userFullName(item.enabledBy) : 'Unknown'),
            },
            {
              label: 'On since',
              field: 'enabledAt',
              sortKey: 'enabledAt',
              format: ({ item }) => <span className="num">{formatDate(item.enabledAt)}</span>,
            },
            {
              label: ' ',
              mobile: 'action',
              format: ({ item }) => (
                <ActionButton
                  onClick={() => manager.requestRemoval({ listing, company: item })}
                  ariaLabel={`Switch ${listing.label} off for ${item.name}`}
                >
                  Remove
                </ActionButton>
              ),
            },
          ]}
        />
      )}
    </Panel>
  );
};
